import React, { useState } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { GOOGLE_MAPS_API_KEY } from 'components/globals';

import { PropertyPhotoPlaceholder } from './PropertyPhoto';

const STREET_VIEW_BANNER_LABEL = 'Street-view image, may not reflect final state.';

type StreetViewEligibility = {
  availableSoon: boolean;
  photoCount: number;
  latitude?: number | null;
  longitude?: number | null;
};

/**
 * Street view only stands in for photo-less available-soon homes (photography
 * gates going live), and only when the home has a geo point. Returns null
 * otherwise, so the caller's existing placeholder path stays intact.
 */
export const streetViewLocationFor = ({
  availableSoon,
  photoCount,
  latitude,
  longitude,
}: StreetViewEligibility): { latitude: number; longitude: number } | null =>
  availableSoon && photoCount === 0 && latitude != null && longitude != null
    ? { latitude, longitude }
    : null;

const streetViewImageUrl = (latitude: number, longitude: number): string => {
  const params = new URLSearchParams({
    size: '640x480',
    location: `${latitude},${longitude}`,
    // Error rather than a generic gray tile when nothing covers the point, and
    // search 100m out like the interactive component does.
    return_error_code: 'true',
    radius: '100',
    key: GOOGLE_MAPS_API_KEY,
  });
  return `https://maps.googleapis.com/maps/api/streetview?${params.toString()}`;
};

type StreetViewPhotoProps = {
  latitude: number;
  longitude: number;
  /** Accessible name for the imagery, e.g. "Street view of 123 Main St". */
  alt: string;
  className?: string;
  /** Hides the street-view disclaimer banner (e.g. dense map markers). */
  hideBanner?: boolean;
  /** Placeholder label when Street View has no coverage for the point. */
  fallbackLabel?: string;
};

/**
 * Street View imagery standing in for a home with no listing photos. The
 * banner carries the may-not-reflect-final-state caveat; Google's own
 * attribution rides on the image itself.
 */
export const StreetViewPhoto: React.FC<StreetViewPhotoProps> = ({
  latitude,
  longitude,
  alt,
  className,
  hideBanner = false,
  fallbackLabel,
}) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <PropertyPhotoPlaceholder state="unavailable" label={fallbackLabel} className={className} />
    );
  }

  return (
    <Box className={cn('relative overflow-hidden bg-od-bg-secondary', className)}>
      <img
        src={streetViewImageUrl(latitude, longitude)}
        alt={alt}
        loading="lazy"
        onError={() => setFailed(true)}
        className="h-full w-full object-cover"
      />
      {hideBanner ? null : (
        <Text
          as="p"
          className="absolute inset-x-0 bottom-0 bg-black/55 px-3 py-1.5 text-body-xs text-white"
        >
          {STREET_VIEW_BANNER_LABEL}
        </Text>
      )}
    </Box>
  );
};
